<script lang="ts">


    import type {Field} from "@/types/types";
    import Input from "@/components/Form/Inputs/Input.svelte";
    import Select from "@/components/Inputs/Select.svelte";
    import Textarea from "@/components/Inputs/Textarea.svelte";
    import {Button} from "@/components/ui/button";
    import RelatedSingle from "@/components/Inputs/RelatedSingle.svelte";
    import Checkbox from "@/components/Inputs/Checkbox.svelte";
    import CustomerTable from "@/components/customers/CustomerTable.svelte";
    import Debug from "@/components/Debug.svelte";
    import JobTable from "@/components/jobs/JobTable.svelte";
    import VehicleTable from "@/components/vehicles/VehicleTable.svelte";
    import SiteTable from "@/components/sites/SiteTable.svelte";
    import FileUpload from "@/components/Inputs/FileUpload.svelte";
    import EmployeeTable from "@/components/employees/EmployeeTable.svelte";
    import RelatedPolymorphic from "@/components/Inputs/RelatedPolymorphic.svelte";
    import MultiRelated from "@/components/Inputs/MultiRelated.svelte";
    import {SvelteMap} from "svelte/reactivity";
    import { toast } from "svelte-sonner";
    import DateTime from '@/components/Inputs/DateTime.svelte';
    import DateOnly from '@/components/Inputs/Date.svelte';
    import { parseFieldOptions } from '@/lib/fieldUtils';
    import { formatDateLocale } from '@/lib/dateUtils';
    import { Info, Pencil } from 'lucide-svelte';
    import PicklistBadge from '@/components/ui/picklist-badge/PicklistBadge.svelte';
    import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

    type Props = {
        field: Field
        editingFieldId?: number
        onSave: (value: any) => void
        error?: string
        record?: Record<string, any> | undefined
    }

    let {field, editingFieldId = $bindable(), onSave, error, record}: Props = $props();

    let editValue = $state(field.value);
    let isSelectCustomerOpen = $state(false);
    let customerLabel: string | null = $state(null);
    let selectedRelatedCustomers = $state(new SvelteMap());
    if(record !== undefined && record.customers !== undefined && record.customers.length > 0){
        record.customers.forEach(c => {
            selectedRelatedCustomers.set(c.id, c.name);
        });
        customerLabel = Array.from(selectedRelatedCustomers.values()).join(", ");
    }

    let isSelectPrimaryCustomerOpen = $state(false);
    let primaryCustomerLabel: string | null = $state(null);
    if(record !== undefined && field.column_name === 'is_primary' && record.primaryCustomer !== null && record.primaryCustomer !== undefined){
        primaryCustomerLabel = record.primaryCustomer.first_name;
    }

    // Organisation field for contacts
    let isSelectOrganisationOpen = $state(false);
    let organisationLabel: string | null = $state(null);
    if(record !== undefined && field.column_name === 'organisation_id' && record.organisation !== null && record.organisation !== undefined){
        organisationLabel = record.organisation.organisation_name ?? record.organisation.name;
    }

    let isSelectJobOpen = $state(false);
    let jobLabel: string | null = $state(null);

    // let isSelectSiteOpen = $state(false);
    // let siteLabel: string | null = $state(null);
    let selectedRelatedJobs = $state(new SvelteMap());
    if(record !== undefined && record.related_jobs !== undefined && record.related_jobs.length > 0){
        record.related_jobs.forEach(c => {
            selectedRelatedJobs.set(c.id, c.title);
        });
        jobLabel = Array.from(selectedRelatedJobs.values()).join(", ");
    }

    // if(record !== undefined && field.column_name === 'site_id' && record.site_id !== null && record.site !== undefined){
    //     siteLabel = record.site.site_name;
    // }


    let isSelectVehicalOpen = $state(false);
    let vehicleLabel: string | null = $state(null);

    let isSelectRelatedOpen = $state(false);
    let relatedModule: string | null = $state(null);
    let relatedLabel: string | null = $state(null);
    if(record !== undefined && field.column_name === 'related_record' && record.related_record_id !== null){
        relatedModule = record.related_record_type;
        relatedLabel = record.related_record_label;
    }

    // Define your component configurations
    const polymorphicFieldConfigs = {
        'App\\Models\\Vehicle': {
            component: VehicleTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    editValue = row?.id ?? null;
                    relatedModule = 'App\\Models\\Vehicle';
                    relatedLabel = row?.registration ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        'App\\Models\\Employee': {
            component: EmployeeTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    editValue = row?.id ?? null;
                    relatedModule = 'App\\Models\\Employee';
                    relatedLabel = row?.name ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        'App\\Models\\Job': {
            component: JobTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    editValue = row?.id ?? null;
                    relatedModule = 'App\\Models\\Job';
                    relatedLabel = row?.title ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        'App\\Models\\Customer': {
            component: CustomerTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    editValue = row?.id ?? null;
                    relatedModule = 'App\\Models\\Customer';
                    relatedLabel = row?.first_name ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
        'App\\Models\\Site': {
            component: SiteTable,
            props: {
                view: "related",
                onRowClick: (row) => {
                    editValue = row?.id ?? null;
                    relatedModule = 'App\\Models\\Site';
                    relatedLabel = row?.site_name ?? "";
                    isSelectRelatedOpen = false;
                }
            }
        },
    };

    let lastError: string | null = null;
    $effect(() => {
        if (error && error !== lastError) {
            toast.error(error);
            lastError = error;
        }
    });

    // Parse select options and find label for current value
    let selectDisplayLabel = $derived.by(() => {
        if (field.ui_type !== 'select') return null;

        // Use backend-provided label if available (for enum-backed fields)
        if (field.value_label) return field.value_label;

        // Fall back to finding label in options
        if (!field.options) return field.value ?? null;

        const parsedOptions = parseFieldOptions(field);
        const matchedOption = parsedOptions.find(opt => opt.value === field.value);
        return matchedOption?.label ?? field.value ?? null;
    });

</script>

<div class="editable-field flex items-top gap-2 pointer group w-full overflow-hidden">
    {#if editingFieldId === field.id && field.editable}
        {#if field.ui_type === 'related' && (field.column_name === 'is_primary' || field.column_name === 'related_customer_id')}
            <RelatedSingle
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectPrimaryCustomerOpen}
                bind:valLabel={primaryCustomerLabel}
            >
                <CustomerTable
                    view="related"
                    onRowClick={(row) => {
                                        editValue = row?.id ?? null;
                                        primaryCustomerLabel = row?.first_name ?? '';
                                        isSelectPrimaryCustomerOpen = false;
                                        field.value_label = primaryCustomerLabel;
                                    }}
                />
            </RelatedSingle>


        <!--{:else if field.ui_type === 'related' && field.column_name === 'site_id'}-->
        <!--    <RelatedSingle-->
        <!--        label={field.label}-->
        <!--        bind:value={editValue}-->
        <!--        error={error}-->
        <!--        bind:isOpen={isSelectSiteOpen}-->
        <!--        bind:valLabel={siteLabel}-->
        <!--    >-->
        <!--        <SiteTable-->
        <!--            view="related"-->
        <!--            onRowClick={(row) => {-->
        <!--                                editValue = row?.id ?? null;-->
        <!--                                siteLabel = row?.site_name ?? '';-->
        <!--                                isSelectSiteOpen = false;-->
        <!--                            }}-->
        <!--        />-->
        <!--    </RelatedSingle>-->
        {:else if field.ui_type === 'related' && field.column_name === 'job_id'}
            <RelatedSingle
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectJobOpen}
                bind:valLabel={jobLabel}
            >
                <JobTable
                    view="related"
                    onRowClick={(row) => {
                                        editValue= row?.id ?? null;
                                        jobLabel = row?.title ?? '';
                                        isSelectJobOpen = false;
                                    }}
                />
            </RelatedSingle>
        {:else if field.ui_type === 'related' && field.column_name === 'vehicle_id'}
            <RelatedSingle
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectVehicalOpen}
                bind:valLabel={vehicleLabel}
            >
                <VehicleTable
                    view="related"
                    onRowClick={(row) => {
                                                editValue = row?.id ?? null;
                                                vehicleLabel = row?.registration ?? '';
                                                isSelectVehicalOpen = false;
                                            }}
                />
            </RelatedSingle>
        {:else if field.ui_type === 'related' && field.column_name === 'organisation_id'}
            <RelatedSingle
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectOrganisationOpen}
                bind:valLabel={organisationLabel}
            >
                <CustomerTable
                    view="related"
                    typeFilter="organisation"
                    onRowClick={(row) => {
                        editValue = row?.id ?? null;
                        organisationLabel = row?.organisation_name ?? row?.name ?? '';
                        isSelectOrganisationOpen = false;
                        field.value_label = organisationLabel;
                    }}
                />
            </RelatedSingle>
        {:else if field.ui_type === 'polymorphic'}
            <RelatedPolymorphic
                label="Related Record"
                bind:module={relatedModule}
                bind:value={editValue}
                bind:valLabel={relatedLabel}
                error={error}
                bind:isOpen={isSelectRelatedOpen}
                children={polymorphicFieldConfigs}
            />
        {:else if field.ui_type === 'multi_related' && field.column_name === 'customers'}
            <MultiRelated
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectCustomerOpen}
                bind:valLabel={customerLabel}
            >
                <CustomerTable
                    view="multi_related"
                    onRowClick={(row) => {
                                                // $form.customer_id = row?.id ?? null;
                                                // customerLabel = row?.first_name ?? '';
                                                // isSelectCustomerOpen = false;
                                            }}
                    bind:selectedCustomers={selectedRelatedCustomers}
                    onSubmitClick={() => {
                                                editValue = selectedRelatedCustomers.keys().toArray();
                                                customerLabel = Array.from(selectedRelatedCustomers.values()).join(", ");
                                                isSelectCustomerOpen = false;
                                            }}
                />
            </MultiRelated>
        {:else if field.ui_type === 'multi_related' && field.column_name === 'related_jobs'}
            <MultiRelated
                label={field.label}
                bind:value={editValue}
                error={error}
                bind:isOpen={isSelectJobOpen}
                bind:valLabel={jobLabel}
            >
                <JobTable
                    view="multi_related"
                    onRowClick={(row) => {
                                                // $form.customer_id = row?.
                                                // id ?? null;
                                                // customerLabel = row?.first_name ?? '';
                                                // isSelectCustomerOpen = false;
                                            }}
                    bind:selectedJobs={selectedRelatedJobs}
                    onSubmitClick={() => {
                                                editValue = selectedRelatedJobs.keys().toArray();
                                                jobLabel = Array.from(selectedRelatedJobs.values()).join(", ");
                                                isSelectJobOpen = false;
                                            }}
                />
            </MultiRelated>
        {:else if field.ui_type === 'file'}
            <FileUpload
                allowMultiple={false}
                bind:value={editValue}
                label={field.label}
                maxFileSize="30MB"
            />
        {:else if field.ui_type === 'checkbox'}
            <Checkbox
                bind:checked={editValue}
                label={field.label}
                error={error}
            />
        {:else if field.ui_type === 'select'}
            <Select
                label={field.label}
                bind:value={editValue}
                options={parseFieldOptions(field)}
                error={error}
            />
        {:else if field.ui_type === 'textarea'}
            <Textarea
                label={field.label}
                bind:value={editValue}
                error={error}
            />
        {:else if field.ui_type === 'date'}
            <DateOnly
                label={field.label}
                bind:value={editValue}
                error={error}
            />
        {:else if field.ui_type === 'datetime'}
            {@const startDate = field.column_name === 'estimated_job_end' && record?.estimated_job_start
                ? new Date(record.estimated_job_start)
                : field.column_name === 'end_date_time' && record?.start_date_time
                ? new Date(record.start_date_time)
                : null}
            {@const endDate = field.column_name === 'estimated_job_start' && record?.estimated_job_end
                ? new Date(record.estimated_job_end)
                : field.column_name === 'start_date_time' && record?.end_date_time
                ? new Date(record.end_date_time)
                : null}
            {@const minDateValue = startDate ? new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0) : null}
            {@const maxDateValue = endDate ? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59) : null}
            <DateTime
                label={field.label}
                bind:value={editValue}
                error={error}
                minValue={minDateValue}
                maxValue={maxDateValue}
            />

        {:else}
            <Input
                field={field}
                bind:value={editValue}
                error={error}
                onkeydown={(e) => {
                if (e.key === 'Enter') {
                    onSave(editValue);
                } else if (e.key === 'Escape') {
                    editingFieldId = undefined;
                }
            }}
            />
        {/if}
        <div class="flex h-full items-end gap-2 ">
            <Button type="button" onclick={()=>onSave(editValue)}>Save</Button>
            <Button type="button" onclick={()=>editingFieldId = undefined}>Cancel</Button>
        </div>

    {:else}
    <span class="font-medium align-top inline-flex items-center gap-1">
      {field.label ?? '-'}:
      {#if (field as any).description}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Info class="h-3.5 w-3.5 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>
              <p class="max-w-xs text-xs">{(field as any).description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      {/if}
    </span>
        <span>
        {#if field.ui_type === 'related' || field.ui_type === 'polymorphic'}
            <a href="{field.link}" class="text-blue-500 hover:underline">
                {field.value_label ?? '-'}
            </a>
        {:else if field.ui_type === 'file' && field.value}
            <a href="{field.link}" class="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                  {field.value ?? '-'}
            </a>
        {:else if field.ui_type === 'multi_related' && field.value}
<!--            <Debug {field} />-->
            {#each field.links as item, index}
                <a href="{item.link}" class="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                    {item.label}
                </a>{index < field.links.length - 1 ? ', ' : ''}

            {/each}
        {:else if field.ui_type === 'date'}
            {formatDateLocale(field.value, true)}
        {:else if field.ui_type === 'datetime'}
            {formatDateLocale(field.value)}

        {:else if field.ui_type === 'select'}
            {#if field.value_color && selectDisplayLabel}
                <PicklistBadge label={selectDisplayLabel} color={field.value_color} />
            {:else}
                {selectDisplayLabel ?? '-'}
            {/if}
        {:else if field.ui_type === 'checkbox'}
            {field.value ? 'Yes' : 'No'}
        {:else if field.ui_type === 'password'}
            ••••••••
        {:else}
            {field.value ?? '-'}
        {/if}
    </span>
        {#if field.editable}
    <button type="button" class="pencil" onclick={()=>editingFieldId = field.id} title="Edit">
      <Pencil class="size-4 text-muted-foreground hover:text-foreground" />
    </button>
        {/if}

    {/if}
</div>
